import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Tradesperson } from '@/lib/types';
import { useAuth } from '@/contexts/AuthContext';
import { getUserSharedTradespeople, deleteUserTradesperson, addUserTradesperson, getPostcodeTradespeople, getTradesPeopleByUsername, getPopularTradespeople, getRecentTradespeople } from '@/lib/supabase/trades';
import { debug } from '@/lib/utils';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, ExternalLink, UserCheck, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TradePersonCard } from './TradePersonCard';
import { supabase } from '@/integrations/supabase/client';
import { getTradeType, getTradeIconForTrade } from '@/lib/tradeUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';

// Define available list modes
type TradeListMode = 'personal' | 'public' | 'results' | 'search';

// Define data sources
type DataSource = 'user' | 'username' | 'postcode' | 'popular' | 'provided';

interface TradeListProps {
  // Core props
  initialLimit?: number;
  mode?: TradeListMode;
  dataSource?: DataSource;
  title?: string;

  // Data source props
  postcodeOrUsername?: string;
  userId?: string;
  providedTradespeople?: Tradesperson[];

  // Filter props
  categoryFilter?: string | null;
  onCategoryChange?: (category: string | null) => void;
  externalCategoryControl?: boolean;
  showCategoryFilter?: boolean;

  // UI customization
  showTitle?: boolean;
  showShareButton?: boolean;

  // Special handling
  highlightedTradesperson?: Tradesperson | null;
  customEmptyState?: React.ReactNode;

  // Callbacks
  onTradespersonSaved?: (trade: Tradesperson) => void;
  onDataLoaded?: (trades: Tradesperson[]) => void;

  // URL sync features
  syncWithUrl?: boolean;
}

export function TradeList({
  // Core props with defaults
  initialLimit = 4,
  mode = 'personal',
  dataSource = 'user',
  title = 'My TradesPurple',

  // Data source props
  postcodeOrUsername,
  userId,
  providedTradespeople,

  // Filter props
  categoryFilter = null,
  onCategoryChange,
  externalCategoryControl = false,
  showCategoryFilter = true,

  // UI customization
  showTitle = true,
  showShareButton = false,

  // Special handling
  highlightedTradesperson = null,
  customEmptyState,

  // Callbacks
  onTradespersonSaved,
  onDataLoaded,

  // URL sync features
  syncWithUrl = false
}: TradeListProps) {
  // URL and navigation hooks for URL sync feature
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're on tradespurple.com domain
  const isTpDomain = typeof window !== 'undefined' &&
                     window.location.hostname.includes('tradespurple.com');

  // Determine base route for navigation
  const homeRoute = isTpDomain ? '/' : '/tradespurple';

  // Get location state to check if this is just a category filter change
  const isFilterNavigation = location?.state?.skipDataReload === true;

  // Auth context for user info
  const { user, isAuthenticated } = useAuth();

  // Core state
  const [tradespeople, setTradespeople] = useState<Tradesperson[]>(providedTradespeople || []);
  const [filteredTradespeople, setFilteredTradespeople] = useState<Tradesperson[]>(providedTradespeople || []);
  const [isLoading, setIsLoading] = useState(dataSource !== 'provided');
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [expanded, setExpanded] = useState(false);
  const [limit, setLimit] = useState(initialLimit);
  const [username, setUsername] = useState<string | undefined>(undefined);
  const [locationName, setLocationName] = useState<string | null>(null);

  // Category filtering state
  const [activeFilter, setActiveFilter] = useState<string | null>(categoryFilter);
  const [categories, setCategories] = useState<string[]>([]);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Category container ref
  const categoryScrollContainerRef = useRef<HTMLDivElement>(null);

  // Keep a cache of removed tradespeople for undo functionality
  const [removedTradeCache, setRemovedTradeCache] = useState<Record<string, Tradesperson>>({});
  // Keep track of user-tradesperson relation IDs and notes for each tradesperson
  const [relationIds, setRelationIds] = useState<Record<string, string>>({});
  const [tradeNotes, setTradeNotes] = useState<Record<string, string>>({});

  // Load username for sharing when in personal mode
  useEffect(() => {
    if (!user || mode !== 'personal') return;

    async function loadUsername() {
      try {
        const { data } = await supabase
          .from('profiles')
          .select('username')
          .eq('id', user.id)
          .single();

        if (data?.username) {
          setUsername(data.username);
        }
      } catch (error) {
        debug.error('Failed to load username:', error);
      }
    }

    loadUsername();
  }, [user, mode]);

  // Load tradespeople data based on data source
  const loadTradespeople = useCallback(async () => {
    // Skip load if filtering navigation or if we already have provided data
    if (isFilterNavigation || dataSource === 'provided') {
      setIsLoading(false);
      return;
    }

    // Always start with loading state
    setIsLoading(true);
    setError(null);

    try {
      let trades: Tradesperson[] = [];
      let userInfo = null;
      let recentTrades: Tradesperson[] = [];

      // Fetch data based on data source
      switch (dataSource) {
        case 'user': {
          // User's saved tradespeople
          if (!user) {
            setIsLoading(false);
            return;
          }

          // Get recently used tradespeople if we're in personal mode
          if (mode === 'personal') {
            try {
              recentTrades = await getRecentTradespeople(user.id, 3); // Limit to 3 recent trades
            } catch (err) {
              debug.error('Failed to load recent tradespeople:', err);
              // Continue even if this fails
            }
          }

          // Get tradespeople and the relationship IDs and notes in one query
          const { data: userTradeRelations } = await supabase
            .from("user_tradespeople")
            .select(`
              id,
              notes,
              tradesperson:tradesperson_id(*)
            `)
            .eq("user_id", user.id);

          if (userTradeRelations) {
            // Extract tradespeople and build relation ID map
            trades = userTradeRelations.map(relation => relation.tradesperson) as Tradesperson[];
            const idMap: Record<string, string> = {};
            const notesMap: Record<string, string> = {};

            userTradeRelations.forEach(relation => {
              if (relation.tradesperson?.id) {
                idMap[relation.tradesperson.id] = relation.id;
                notesMap[relation.tradesperson.id] = relation.notes || '';
              }
            });

            setRelationIds(idMap);
            // Store notes in state for passing to cards
            setTradeNotes(notesMap);

            // Sort trades alphabetically by org name (tradesperson's business name)
            trades = [...trades].sort((a, b) => {
              // Use organization name for sorting
              const nameA = a.org?.toLowerCase() || '';
              const nameB = b.org?.toLowerCase() || '';
              return nameA.localeCompare(nameB);
            });
          }
          break;
        }

        case 'username': {
          // Tradespeople shared by a specific username
          if (!postcodeOrUsername) {
            throw new Error("Username is required for username data source");
          }

          const result = await getTradesPeopleByUsername(postcodeOrUsername);
          trades = result.tradespeople;
          userInfo = result.userData;

          // Set username for display
          setUsername(postcodeOrUsername);

          if (!result.userData) {
            throw new Error(`No user found with username "${postcodeOrUsername}"`);
          }

          // For results mode, sort by popularity (saved count)
          // For other modes, sort alphabetically by organization name
          if (mode === 'results') {
            trades = [...trades].sort((a, b) => {
              const countA = a.recommendation_count || 0;
              const countB = b.recommendation_count || 0;
              return countB - countA; // Sort by descending count
            });
          } else {
            trades = [...trades].sort((a, b) => {
              const nameA = a.org?.toLowerCase() || '';
              const nameB = b.org?.toLowerCase() || '';
              return nameA.localeCompare(nameB);
            });
          }
          break;
        }

        case 'postcode': {
          // Tradespeople in a specific postcode area
          if (!postcodeOrUsername) {
            throw new Error("Postcode is required for postcode data source");
          }

          trades = await getPostcodeTradespeople(postcodeOrUsername);
          // Set location for display
          setLocationName(postcodeOrUsername.toUpperCase());

          // For results mode, sort by popularity (saved count)
          // For other modes, sort alphabetically by organization name
          if (mode === 'results') {
            trades = [...trades].sort((a, b) => {
              const countA = a.recommendation_count || 0;
              const countB = b.recommendation_count || 0;
              return countB - countA; // Sort by descending count
            });
          } else {
            trades = [...trades].sort((a, b) => {
              const nameA = a.org?.toLowerCase() || '';
              const nameB = b.org?.toLowerCase() || '';
              return nameA.localeCompare(nameB);
            });
          }
          break;
        }

        case 'popular': {
          // Popular/featured tradespeople (already sorted by popularity in getPopularTradespeople)
          trades = await getPopularTradespeople();
          break;
        }

        default: {
          // If we reach here with 'provided' source, data should already be set
          if (providedTradespeople) {
            trades = providedTradespeople;

            // For results mode, sort by popularity (saved count)
            // For other modes, sort alphabetically by organization name
            if (mode === 'results') {
              trades = [...trades].sort((a, b) => {
                const countA = a.recommendation_count || 0;
                const countB = b.recommendation_count || 0;
                return countB - countA; // Sort by descending count
              });
            } else {
              trades = [...trades].sort((a, b) => {
                const nameA = a.org?.toLowerCase() || '';
                const nameB = b.org?.toLowerCase() || '';
                return nameA.localeCompare(nameB);
              });
            }
          }
        }
      }

      // Update state with the fetched data
      setTradespeople(trades);

      // Set filtered tradespeople based on active filter
      if (!activeFilter) {
        setFilteredTradespeople(trades);
      } else {
        const filtered = trades.filter(trade =>
          getTradeType(trade)?.toLowerCase() === activeFilter.toLowerCase()
        );
        setFilteredTradespeople(filtered);
      }

      // Extract categories from trades
      if (trades.length > 0) {
        const uniqueCategories = Array.from(
          new Set(
            trades
              .map(trade => getTradeType(trade))
              .filter(Boolean) as string[]
          )
        );
        setCategories(uniqueCategories);
      }

      // Invoke the callback with the loaded data
      if (onDataLoaded) {
        onDataLoaded(trades);
      }
    } catch (error) {
      debug.error(`Failed to load tradespeople (${dataSource}):`, error);
      setError(error instanceof Error ? error.message : 'Failed to load tradespeople.');
      setTradespeople([]);
      setFilteredTradespeople([]);
    } finally {
      setIsLoading(false);
    }
  }, [user, dataSource, postcodeOrUsername, providedTradespeople, activeFilter, isFilterNavigation, onDataLoaded, mode]);

  // Initial data load
  useEffect(() => {
    loadTradespeople();
  }, [dataSource, postcodeOrUsername, userId, loadTradespeople]);

  // Reset state when data source changes
  useEffect(() => {
    // Reset state when data source changes (except for filtering)
    if (!isFilterNavigation) {
      setActiveFilter(categoryFilter);
      setError(null);
    }
  }, [dataSource, categoryFilter, isFilterNavigation]);

  // Helper function to normalize trade types for URL
  const normalizeTradeType = (type: string): string => {
    return type.trim().toLowerCase().replace(/-/g, ' ').replace(/\s+/g, ' ');
  };

  // UK postcode regex - used to determine if the parameter is a postcode or username
  const isUKPostcode = (value: string | undefined) => {
    if (!value) return false;
    const postcodeRegex = /^[A-Z]{1,2}[0-9]{1,2}$/i;
    return postcodeRegex.test(value);
  };

  // Category scrolling logic
  const checkScrollPosition = useCallback(() => {
    const container = categoryScrollContainerRef.current;
    if (container) {
      // Can scroll left if scrolled right at all
      setCanScrollLeft(container.scrollLeft > 0);

      // Can scroll right if there's more content to scroll to
      setCanScrollRight(
        container.scrollWidth > container.clientWidth &&
        container.scrollLeft < container.scrollWidth - container.clientWidth
      );
    }
  }, []);

  // Set up scroll event listener for category container
  useEffect(() => {
    const container = categoryScrollContainerRef.current;
    if (!container) return;

    // Check initial scroll position
    checkScrollPosition();

    // Add scroll event listener
    container.addEventListener('scroll', checkScrollPosition);

    // Check again after categories loaded (as content width may change)
    if (categories.length > 0) {
      setTimeout(checkScrollPosition, 100);
    }

    return () => {
      container.removeEventListener('scroll', checkScrollPosition);
    };
  }, [checkScrollPosition, categories.length]);

  // Scroll action handlers
  const handleScrollLeft = () => {
    if (categoryScrollContainerRef.current) {
      categoryScrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (categoryScrollContainerRef.current) {
      categoryScrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  // Category selection handlers
  const handleSelectFilter = (category: string) => {
    if (externalCategoryControl) {
      // If parent component controls the filter
      if (onCategoryChange) {
        // If it's already active, clear it
        if (categoryFilter === category) {
          onCategoryChange(null);
        } else {
          onCategoryChange(category);
        }
      }
    } else {
      // Internal state management
      if (activeFilter === category) {
        clearFilter();
      } else {
        selectFilter(category);
      }
    }
  };

  // Select a filter category (with optional URL sync)
  const selectFilter = (category: string) => {
    if (process.env.NODE_ENV === 'development') {
      debug.log('[TradeList] Filter selection:', { category, activeFilter });
    }

    // Update filtered data immediately to prevent flicker
    const filtered = tradespeople.filter(trade =>
      getTradeType(trade)?.toLowerCase() === category.toLowerCase()
    );
    setFilteredTradespeople(filtered);

    // Set the active filter state
    setActiveFilter(category);

    // URL synchronization (only if enabled and in results mode)
    if (syncWithUrl && mode === 'results' && postcodeOrUsername) {
      // Format the category for URL (lowercase, hyphens instead of spaces)
      const formattedCategory = category.toLowerCase().replace(/\s+/g, '-');

      // Generate the route with the selected category
      const route = isTpDomain
        ? `/${postcodeOrUsername}/${formattedCategory}`
        : `/tradespurple/${postcodeOrUsername}/${formattedCategory}`;

      if (process.env.NODE_ENV === 'development') {
        debug.log('[TradeList] URL Navigation:', {
          from: location.pathname,
          to: route,
          category,
          isTpDomain
        });
      }

      // Navigate with skipDataReload to prevent unnecessary data fetching
      navigate(route, {
        replace: true,
        state: {
          skipDataReload: true,
          previousCategory: activeFilter,
          newCategory: category
        }
      });
    }
  };

  // Clear filter (with optional URL sync)
  const clearFilter = () => {
    if (process.env.NODE_ENV === 'development') {
      debug.log('[TradeList] Clear filter:', { activeFilter });
    }

    // Clear the filter directly
    setActiveFilter(null);

    // Update filtered list to show all tradespeople
    setFilteredTradespeople(tradespeople);

    // URL synchronization (only if enabled and in results mode)
    if (syncWithUrl && mode === 'results' && postcodeOrUsername) {
      // Generate route without category parameter
      const route = isTpDomain
        ? `/${postcodeOrUsername}`
        : `/tradespurple/${postcodeOrUsername}`;

      if (process.env.NODE_ENV === 'development') {
        debug.log('[TradeList] Clear URL Navigation:', {
          from: location.pathname,
          to: route,
          isTpDomain
        });
      }

      // Navigate with skipDataReload
      navigate(route, {
        replace: true,
        state: {
          skipDataReload: true,
          clearedCategory: activeFilter
        }
      });
    }
  };

  // Handle removal of a tradesperson (only in personal mode)
  const handleRemoveTrade = async (tradeId: string) => {
    if (!user || mode !== 'personal') return;

    // Find the tradesperson to cache for potential undo
    const tradeToRemove = tradespeople.find(t => t.id === tradeId);
    if (!tradeToRemove) return;

    // Get the relation ID for this tradesperson
    const relationId = relationIds[tradeId];
    if (!relationId) {
      toast.error("Failed to remove tradesperson: relationship not found");
      return;
    }

    try {
      // Cache the tradesperson before removal
      setRemovedTradeCache(prev => ({
        ...prev,
        [tradeId]: tradeToRemove
      }));

      // Delete the relationship
      await deleteUserTradesperson(relationId);

      // Update the UI by removing the tradesperson from the list
      setTradespeople(trades => trades.filter(t => t.id !== tradeId));

      // If we're showing expanded view but this would reduce below the limit, update limit
      if (expanded && tradespeople.length <= initialLimit + 1) {
        setExpanded(false);
        setLimit(initialLimit);
      }

    } catch (error) {
      debug.error('Failed to remove tradesperson:', error);
      throw error; // Rethrow for the calling component to handle
    }
  };

  // Handle undoing a removal (only in personal mode)
  const handleUndoRemove = async (tradeId: string) => {
    if (!user || mode !== 'personal') return;

    // Get the cached tradesperson
    const tradeToRestore = removedTradeCache[tradeId];
    if (!tradeToRestore) {
      toast.error("Failed to restore tradesperson: not found in cache");
      return;
    }

    try {
      // Add the tradesperson back to the user's list
      await addUserTradesperson(user.id, tradeId, {
        preferred: false,
        notes: ""
      });

      // Refresh the list to get the new relation ID
      await loadTradespeople();

      // Remove from the cache
      const { [tradeId]: _, ...restCache } = removedTradeCache;
      setRemovedTradeCache(restCache);

    } catch (error) {
      debug.error('Failed to restore tradesperson:', error);
      throw error; // Rethrow for the calling component to handle
    }
  };

  // Toggle show more/less list items
  const toggleExpand = () => {
    setExpanded(!expanded);
    if (!expanded) {
      setLimit(tradespeople.length);
    } else {
      setLimit(initialLimit);
    }
  };

  // Helper to determine card mode based on component mode
  const getCardMode = (): 'saved' | 'public' => {
    switch (mode) {
      case 'personal':
        return 'saved';
      case 'public':
      case 'results':
      case 'search':
        return 'public';
      default:
        return 'public';
    }
  };

  // Handle save action on public cards
  const handleSaveTrade = (trade: Tradesperson) => {
    // Call the provided callback if available
    if (onTradespersonSaved) {
      onTradespersonSaved(trade);
    }

    // If in personal mode, refresh data to show the saved trade
    if (mode === 'personal') {
      loadTradespeople();
    }
  };

  // Generate title text based on mode
  const getTitleText = () => {
    if (title) return title;

    switch (mode) {
      case 'personal':
        return `My TradesPurple${tradespeople.length > 0 ? ` (${tradespeople.length})` : ''}`;
      case 'results':
        if (username) {
          return activeFilter
            ? `${username}'s recommended ${activeFilter}`
            : `${username}'s recommended tradespeople`;
        } else if (postcodeOrUsername && isUKPostcode(postcodeOrUsername)) {
          return activeFilter
            ? `${activeFilter} near ${postcodeOrUsername.toUpperCase()}`
            : `Tradespeople near ${postcodeOrUsername.toUpperCase()}`;
        } else {
          return activeFilter
            ? `Popular ${activeFilter}`
            : "Popular tradespeople";
        }
      case 'public':
        return "Community Recommendations";
      case 'search':
        return "Search Results";
      default:
        return "Tradespeople";
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="space-y-3">
        {showTitle && (
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold" style={{ color: "#5D4A9C" }}>{getTitleText()}</h2>
          </div>
        )}
        <div className="space-y-3">
          {[...Array(initialLimit)].map((_, i) => (
            <div key={i} className="p-4 border border-purple-200 rounded-lg bg-white animate-pulse">
              <div className="h-5 bg-purple-200 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-purple-100 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="space-y-3">
        {showTitle && (
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold" style={{ color: "#5D4A9C" }}>{getTitleText()}</h2>
          </div>
        )}
        <div className="p-6 bg-white rounded-xl text-purple-900 border-2 border-purple-900 shadow-md">
          <p className="font-bold">{error}</p>
          <div className="mt-4 flex flex-col gap-2">
            <Button
              onClick={() => loadTradespeople()}
              className="bg-purple-700 hover:bg-purple-800 shadow-md"
            >
              Try Again
            </Button>
            {mode === 'results' && (
              <Button
                onClick={() => navigate(homeRoute)}
                variant="outline"
                className="border-2 border-purple-700 text-purple-900 hover:bg-purple-50"
              >
                Back to Search
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render empty state
  if (tradespeople.length === 0) {
    return (
      <div className="space-y-3">
        {showTitle && (
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold" style={{ color: "#5D4A9C" }}>{getTitleText()}</h2>
          </div>
        )}

        {customEmptyState ? (
          customEmptyState
        ) : (
          mode === 'personal' ? (
            <div className="p-6 border-2 border-purple-300 rounded-xl bg-white shadow-sm">
              <div className="text-center">
                <div className="inline-flex rounded-full bg-purple-100 p-3 mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-purple-900 mb-2">Your TradesPurple is empty</h2>
                <p className="text-purple-700 mb-4">Start building your list of trusted tradespeople.</p>

                {/* Big prominent Add Trade Button */}
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('tradespurple-action', { detail: { action: 'add' } }))}
                  className="flex items-center justify-center w-full bg-purple-700 hover:bg-purple-800 text-white font-medium py-3 px-4 rounded-lg my-5 shadow-md transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Your First Tradesperson
                </button>
              </div>

              {/* Benefits Section */}
              <div className="mt-8 mb-2">
                <h3 className="text-purple-900 font-semibold text-lg mb-4">Why add tradespeople?</h3>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div className="flex items-start p-4 rounded-lg bg-purple-50 border border-purple-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="rounded-full bg-purple-100 p-2 mr-3 flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <p className="text-purple-800 font-medium">SAVE</p>
                    <p className="text-purple-700 text-sm mt-1">One place to save local trusted trades - no more searching for that handyman's number</p>
                  </div>
                </div>

                <div className="flex items-start p-4 rounded-lg bg-purple-50 border border-purple-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="rounded-full bg-purple-100 p-2 mr-3 flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <p className="text-purple-800 font-medium">CONTACT</p>
                    <p className="text-purple-700 text-sm mt-1">The fastest way to message multiple tradespeople at once - we'll even draft it for you</p>
                  </div>
                </div>

                <div className="flex items-start p-4 rounded-lg bg-purple-50 border border-purple-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="rounded-full bg-purple-100 p-2 mr-3 flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <p className="text-purple-800 font-medium">SHARE</p>
                    <p className="text-purple-700 text-sm mt-1">Share your list with neighbors and help everyone use the best tradespeople easily</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Card className="p-6 text-center border-2 border-purple-900 rounded-xl shadow-md">
              <CardContent>
                {mode === 'results' && !isAuthenticated ? (
                  // Simplified empty state for postcode results
                  <>
                    <div className="rounded-lg bg-purple-700 p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center shadow-md">
                      <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                      </svg>
                    </div>

                    <h3 className="text-xl font-bold mb-2" style={{ color: "#5D4A9C" }}>Be the Pioneer!</h3>

                    <p className="text-purple-700 mb-6 text-lg">
                      No tradespeople have been added in
                      {locationName
                        ? ` the ${locationName} area`
                        : ' this area'} yet.
                    </p>

                    <div className="mb-4">
                      <Button
                        onClick={() => navigate('/auth/signup')}
                        className="bg-white hover:bg-purple-50 text-purple-700 font-medium rounded-lg transition shadow-sm border-2 border-purple-700 px-6 py-3"
                      >
                        Add my first tradesperson
                      </Button>
                    </div>

                    <div className="bg-purple-50/70 py-4 px-6 rounded-lg max-w-md mx-auto">
                      <p className="text-purple-700 text-sm">
                        Your recommendations will help others in your community find trusted local trades.
                      </p>
                    </div>
                  </>
                ) : (
                  // Simple, clean empty state that matches the design - same for logged-in and logged-out
                  <>
                    <div className="rounded-lg bg-purple-700 p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center shadow-md">
                      <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                      </svg>
                    </div>

                    <h3 className="text-xl font-bold mb-2" style={{ color: "#5D4A9C" }}>Be the Pioneer!</h3>

                    <p className="text-purple-700 mb-6 text-lg">
                      No tradespeople have been added in
                      {locationName
                        ? ` the ${locationName} area`
                        : ' this area'} yet.
                    </p>

                    <div className="mb-4">
                      <Button
                        onClick={() => isAuthenticated
                          ? window.dispatchEvent(new CustomEvent('tradespurple-action', { detail: { action: 'add' } }))
                          : navigate('/auth/signup')}
                        className="bg-white hover:bg-purple-50 text-purple-700 font-medium rounded-lg transition shadow-sm border-2 border-purple-700 px-6 py-3"
                      >
                        {isAuthenticated ? "Add a tradesperson" : "Add my first tradesperson"}
                      </Button>
                    </div>

                    <div className="bg-purple-50/70 py-4 px-6 rounded-lg max-w-md mx-auto">
                      <p className="text-purple-700 text-sm">
                        Your recommendations will help others in your community find trusted local trades.
                      </p>
                    </div>

                    {mode === 'results' && (
                      <Button
                        onClick={() => navigate(homeRoute)}
                        className="bg-purple-700 hover:bg-purple-800 shadow-md px-6 py-2 text-lg mt-4"
                      >
                        Go Back
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {showTitle && (
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-bold" style={{ color: "#5D4A9C" }}>
            {getTitleText()}
          </h2>
        </div>
      )}

      {/* Category Filter - only show when showCategoryFilter is true */}
      {categories.length > 0 && showCategoryFilter && (
        <div className={`${mode === 'results' ? 'mb-3 -mt-3' : 'mb-3 mt-2'}`}>
          {/* For results mode, we integrate with the outer container in ResultsPage */}
          <div className={`relative ${mode === 'results' ? 'bg-white rounded-lg p-2' : ''}`}>
            {/* Category list with navigation arrows */}
            <div className="relative flex items-center">
              {/* Left scroll arrow - only visible when we can scroll left */}
              {canScrollLeft && (
                <button
                  className="absolute left-0 z-20 bg-white/80 backdrop-blur-sm rounded-full shadow-md w-8 h-8 flex items-center justify-center text-purple-700 hover:bg-purple-50"
                  onClick={handleScrollLeft}
                  aria-label="Scroll left"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
              )}

              {/* Scrollable container - hide scrollbar */}
              <div
                ref={categoryScrollContainerRef}
                className="overflow-x-auto mx-8 no-scrollbar"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                <div className="flex items-center py-2 px-1 min-w-full">
                  {/* All category icons */}
                  {categories.map((category) => {
                    const isActive = externalCategoryControl
                      ? categoryFilter === category
                      : activeFilter === category;
                    const TradeIcon = getTradeIconForTrade({ trade_type: category } as Tradesperson);

                    return (
                      <button
                        key={category}
                        onClick={() => handleSelectFilter(category)}
                        title={isActive ? `${category} (tap again to clear)` : category}
                        aria-label={isActive ? `Clear ${category} filter` : `Filter by ${category}`}
                        className={`flex items-center justify-center rounded-full w-14 h-14 flex-shrink-0 mr-4 transition-colors ${
                          isActive
                            ? 'bg-purple-700 shadow-md'
                            : 'bg-purple-100 hover:bg-purple-200'
                        }`}
                      >
                        <TradeIcon className={`h-6 w-6 ${isActive ? 'text-white' : 'text-purple-700'}`} />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Right scroll arrow - only visible when we can scroll right */}
              {canScrollRight && (
                <button
                  className="absolute right-0 z-20 bg-white/80 backdrop-blur-sm rounded-full shadow-md w-8 h-8 flex items-center justify-center text-purple-700 hover:bg-purple-50"
                  onClick={handleScrollRight}
                  aria-label="Scroll right"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* No results when filtered */}
      {filteredTradespeople.length === 0 && (categoryFilter || activeFilter) && (
        <Card className="p-6 text-center border-2 border-purple-900 rounded-xl shadow-md">
          <CardContent>
            <div className="rounded-lg bg-purple-700 p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center shadow-md">
              <Filter className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: "#5D4A9C" }}>No matches found</h3>
            <p className="text-purple-700 mb-4 text-lg">
              No {externalCategoryControl ? categoryFilter : activeFilter} found. Try a different trade type.
            </p>

            {!isAuthenticated && mode !== 'personal' && (
              <div className="mb-6 mt-6 bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold mb-2" style={{ color: "#5D4A9C" }}>Be the first to add a {externalCategoryControl ? categoryFilter : activeFilter}</h4>
                <p className="text-purple-700 mb-4">
                  Sign up to add the first {externalCategoryControl ? categoryFilter : activeFilter} in this area and help your neighbours.
                </p>

                <div className="flex justify-center">
                  <Button
                    onClick={() => navigate('/auth/signup')}
                    className="mt-2 bg-white hover:bg-purple-50 text-purple-700 font-medium rounded-lg transition shadow-sm border-2 border-purple-700 px-6 py-3"
                  >
                    Add my first tradesperson
                  </Button>
                </div>
              </div>
            )}

            <Button
              onClick={clearFilter}
              className="bg-purple-700 hover:bg-purple-800 shadow-md px-6 py-2 text-lg"
            >
              Clear Filter
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Highlighted tradesperson section */}
      {filteredTradespeople.length > 0 && highlightedTradesperson && (
        <div className="mb-6">
          <div className="bg-purple-100 p-3 rounded-lg mb-2 border border-purple-300">
            <p className="text-purple-700 text-sm font-medium">
              <UserCheck className="h-4 w-4 mr-1 inline-block" />
              Recommended tradesperson
            </p>
          </div>
          <TradePersonCard
            key={`highlighted-${highlightedTradesperson.id}`}
            trade={highlightedTradesperson}
            mode={getCardMode()}
            postcode={isUKPostcode(postcodeOrUsername) ? postcodeOrUsername : undefined}
            onSave={handleSaveTrade}
            notes={mode === 'personal' ? tradeNotes[highlightedTradesperson.id] : undefined}
            relationId={mode === 'personal' ? relationIds[highlightedTradesperson.id] : undefined}
          />

          {filteredTradespeople.length > 0 && (
            <div className="bg-gray-100 p-3 rounded-lg mt-6 mb-2">
              <p className="text-gray-700 text-sm text-center">
                More recommended tradespeople from {username || postcodeOrUsername}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Main tradesperson list */}
      {filteredTradespeople.length > 0 && (
        <div className="space-y-3">
          {/* Recent Tradespeople Section (only for personal mode on logged-in page) */}
          {mode === 'personal' && (
            <>
              {/* Get recent trades from the filtered trades */}
              {(() => {
                // Get recent trades from API call
                const recentTradesIds = new Set(
                  filteredTradespeople
                    .slice(0, 3) // Limit to 3 recent trades
                    .filter(trade => trade.created_at &&
                      new Date(trade.created_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)) // Last 30 days
                    .map(trade => trade.id)
                );

                // If we have recent trades, show them first
                if (recentTradesIds.size > 0) {
                  const recentItems = filteredTradespeople
                    .filter(trade => recentTradesIds.has(trade.id) &&
                      (!highlightedTradesperson || trade.id !== highlightedTradesperson.id));

                  if (recentItems.length > 0) {
                    return (
                      <div>
                        <h3 className="text-sm font-medium text-purple-700 mb-2">Recently Used</h3>
                        <div className="space-y-3 mb-4">
                          {recentItems.map((trade) => (
                            <TradePersonCard
                              key={`recent-${trade.id}`}
                              trade={trade}
                              username={username}
                              mode={getCardMode()}
                              postcode={isUKPostcode(postcodeOrUsername) ? postcodeOrUsername : undefined}
                              onSave={handleSaveTrade}
                              onRemove={mode === 'personal' ? () => handleRemoveTrade(trade.id) : undefined}
                              onUndoRemove={mode === 'personal' ? () => handleUndoRemove(trade.id) : undefined}
                              notes={mode === 'personal' ? tradeNotes[trade.id] : undefined}
                              relationId={mode === 'personal' ? relationIds[trade.id] : undefined}
                            />
                          ))}
                        </div>
                        <h3 className="text-sm font-medium text-purple-700 mb-2">All Tradespeople</h3>
                      </div>
                    );
                  }
                }

                return null;
              })()}

              {/* Main alphabetical list */}
              {(() => {
                // Filter out the highlighted tradesperson and recent trades
                const recentTradesIds = new Set(
                  filteredTradespeople
                    .slice(0, 3) // Limit to 3 recent trades
                    .filter(trade => trade.created_at &&
                      new Date(trade.created_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
                    .map(trade => trade.id)
                );

                return filteredTradespeople
                  .filter(trade => {
                    // Skip highlighted
                    if (highlightedTradesperson && trade.id === highlightedTradesperson.id) {
                      return false;
                    }

                    // Skip trades that are already shown in the recent section
                    if (recentTradesIds.has(trade.id)) {
                      return false;
                    }

                    // Include all remaining trades for the main list
                    return true;
                  })
                  .slice(0, limit)
                  .map((trade) => (
                    <TradePersonCard
                      key={trade.id}
                      trade={trade}
                      username={username}
                      mode={getCardMode()}
                      postcode={isUKPostcode(postcodeOrUsername) ? postcodeOrUsername : undefined}
                      onSave={handleSaveTrade}
                      onRemove={mode === 'personal' ? () => handleRemoveTrade(trade.id) : undefined}
                      onUndoRemove={mode === 'personal' ? () => handleUndoRemove(trade.id) : undefined}
                      notes={mode === 'personal' ? tradeNotes[trade.id] : undefined}
                      relationId={mode === 'personal' ? relationIds[trade.id] : undefined}
                    />
                  ));
              })()}
            </>
          )}

          {/* All other modes just show the sorted list */}
          {mode !== 'personal' &&
            filteredTradespeople
              // Filter out the highlighted tradesperson if present
              .filter(trade => !highlightedTradesperson || trade.id !== highlightedTradesperson.id)
              .slice(0, limit)
              .map((trade) => (
                <TradePersonCard
                  key={trade.id}
                  trade={trade}
                  username={username}
                  mode={getCardMode()}
                  postcode={isUKPostcode(postcodeOrUsername) ? postcodeOrUsername : undefined}
                  onSave={handleSaveTrade}
                  onRemove={mode === 'personal' ? () => handleRemoveTrade(trade.id) : undefined}
                  onUndoRemove={mode === 'personal' ? () => handleUndoRemove(trade.id) : undefined}
                  notes={mode === 'personal' ? tradeNotes[trade.id] : undefined}
                  relationId={mode === 'personal' ? relationIds[trade.id] : undefined}
                />
              ))
          }
        </div>
      )}

      {/* Show more/less button - different text for personal mode */}
      {filteredTradespeople.length > initialLimit && (
        <Button
          onClick={toggleExpand}
          variant="ghost"
          className="w-full h-12 text-purple-700 hover:text-purple-800 hover:bg-purple-50 rounded-xl flex items-center justify-center"
        >
          {expanded ? (
            <>
              Show less
              <ChevronUp className="h-5 w-5 ml-1.5 stroke-[2.5]" />
            </>
          ) : (
            <>
              {mode === 'personal' ? (
                <>
                  Show all tradespeople
                  <ChevronDown className="h-5 w-5 ml-1.5 stroke-[2.5]" />
                </>
              ) : (
                <>
                  Show {filteredTradespeople.length - initialLimit} more
                  <ChevronDown className="h-5 w-5 ml-1.5 stroke-[2.5]" />
                </>
              )}
            </>
          )}
        </Button>
      )}

      {/* Share button */}
      {showShareButton && mode !== 'personal' && filteredTradespeople.length > 0 && (
        <div className="mt-8 flex flex-col items-center">
          <Button
            className="bg-purple-700 hover:bg-purple-800 text-white shadow-md py-3 px-6 text-lg rounded-md border-2 border-purple-900"
            onClick={() => {
              // Create a shareable URL that works for current domain
              const shareDomain = isTpDomain ? 'https://tradespurple.com' : 'https://househeld.co/tradespurple';

              // Use the original username case from the database if available
              const displayUsername = username || postcodeOrUsername;

              const shareablePath = `${displayUsername}${activeFilter ? `/${activeFilter.toLowerCase().replace(/\s+/g, '-')}` : ''}`;
              const shareableUrl = `${shareDomain}/${shareablePath}`;

              navigator.clipboard.writeText(shareableUrl);
              toast.success('Link copied to clipboard!');
            }}
          >
            <ExternalLink className="h-5 w-5 mr-2" />
            Share This List
          </Button>
        </div>
      )}
    </div>
  );
}
